import { Box, Button, FullPageSpinner, HStack, Text, VStack } from '@elkaso-app/web-design';
import { useGetInvoiceScanByIdApi } from 'apis/invoice-scanning';
import { useCallback, useEffect, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { DiscrepanciesSection } from '../components/discrepancies-section';
import { DocumentViewer } from '../components/document-viewer';
import { ItemsTable } from '../components/items-table';
import { DeleteScanModal } from '../components/modals/delete-scan-modal';
import { ScanHeaderCard } from '../components/scan-header-card';
import { StatusCards } from '../components/status-cards';
import { ValidationActions } from '../components/validation-actions';

export const InvoiceScanDetailScreen = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isLoading, data: scanResponse } = useGetInvoiceScanByIdApi(id);
  const scan = scanResponse?.data;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    dispatch(changeTitle('Invoice Scan Detail'));
  }, [dispatch]);

  const handleBack = useCallback(() => {
    history.push('/app/invoice-scanning/list');
  }, [history]);

  const handleDeleteClose = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  if (isLoading) return <FullPageSpinner />;
  if (!scan) return <Text p='lg'>Invoice scan not found.</Text>;

  return (
    <VStack spacing='lg' align='stretch' p='lg'>
      {/* Back button + Delete */}
      <HStack justify='space-between'>
        <Button size='sm' variant='ghost' leftIcon={<FiArrowLeft />} onClick={handleBack}>
          Back to List
        </Button>
        <Button size='sm' colorScheme='red' variant='outline' onClick={() => setIsDeleteModalOpen(true)}>
          Delete Scan
        </Button>
      </HStack>

      {/* Two-column layout */}
      <HStack spacing='lg' align='start' flexWrap={{ base: 'wrap', lg: 'nowrap' }}>
        {/* Left: Document viewer */}
        <Box w={{ base: '100%', lg: '40%' }} position={{ lg: 'sticky' }} top={{ lg: '80px' }}>
          <DocumentViewer s3Url={scan.s3Url} mimeType={scan.mimeType} originalFileName={scan.originalFileName} />
        </Box>

        {/* Right: Data sections */}
        <VStack spacing='lg' align='stretch' w={{ base: '100%', lg: '60%' }}>
          <ScanHeaderCard scan={scan} />
          <StatusCards processingStatus={scan.status} validationStatus={scan.validationStatus} />
          <ValidationActions scanId={scan.id} currentStatus={scan.validationStatus} />
          <ItemsTable items={scan.items} />
          <DiscrepanciesSection
            discrepancies={scan.discrepancies}
            validationErrors={scan.validationErrors}
            processingError={scan.processingError}
          />
        </VStack>
      </HStack>

      {/* Delete modal */}
      <DeleteScanModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteClose}
        onDeleteSuccess={handleBack}
        scanId={scan.id}
        invoiceNumber={scan.invoiceNumber}
        vendorName={scan.vendorName}
      />
    </VStack>
  );
};
